import classNames from 'classnames';
import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAppDispatch } from 'app/hooks';
import Icon from 'components/icons/Icon';
import { IconSymbol } from 'components/icons/Icon.types';
import Error404 from 'components/navigation/Error404';
import { useUser } from 'selectors/userSelectors';
import { useUsers } from 'selectors/usersSelectors';

import FileUpload from 'components/files/FileUpload';
import {
  doFormMsg,
  FormMessage,
  FormMsg,
  NoFormMsg,
} from 'components/forms/FormMessage';
import {
  MarkdownContent,
  MarkdownExplainer,
} from 'components/markdown/MarkdownContent';
import { useConfirmDelete } from 'components/modal/forms/ConfirmDeleteForm';
import LiveContentWarning from 'components/page/LiveContentWarning';
import { IFile } from 'features/files/fileAPI';
import { imageFileTypes } from 'features/files/fileSlice';
import {
  getPublicationAnchorId,
  getPublicationCitation,
  sortPublicationsByDate,
} from 'features/publication/publicationSlice';
import { IMember } from 'features/team/memberAPI';
import {
  deleteMemberAsync,
  getMemberAnchorId,
  patchMemberAsync,
} from 'features/team/memberSlice';
import { canEdit } from 'features/user/userSlice';
import { toDateInputValue } from 'helpers/civictech';
import { MEMBER_STATUSES } from 'helpers/consts';
import { sortBy } from 'helpers/sorting';
import { Form } from 'react-bootstrap';
import { useSite } from 'selectors/siteSelectors';
import '../static/StaticPage.scss';
import './../page/Pages.scss';

type MemberProps = {
  member: IMember;
  className?: string;
  asPicture?: boolean;
};

export default function Member(props: MemberProps) {
  const { member, className, asPicture = false } = props;

  const { locale, user, userIsManager } = useUser();
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { projects, publications } = useSite();
  const { users } = useUsers();
  const [file, setFile] = useState<File | undefined>(undefined);
  const [saved, setSaved] = useState<boolean>(true);
  const [previewSrc, setPreviewSrc] = useState<string | null>(
    member.image?.uri ?? null,
  );

  const [editing, setEditing] = useState<boolean>(false);
  const [notfound, setNotfound] = useState<boolean>(false);
  const confirmDelete = useConfirmDelete();
  const [formMsg, setFormMsg] = useState<FormMsg>(NoFormMsg);
  const [nameHasContent, setNameHasContent] = useState<boolean>(
    Boolean(member.name),
  );
  const [firstnameHasContent, setFirstnameHasContent] = useState<boolean>(
    Boolean((member as IMember & { firstname?: string }).firstname),
  );
  const [functionEnHasContent, setFunctionEnHasContent] = useState<boolean>(
    Boolean(member.function_en),
  );
  const [functionNlHasContent, setFunctionNlHasContent] = useState<boolean>(
    Boolean(member.function_nl),
  );
  const [orcidHasContent, setOrcidHasContent] = useState<boolean>(
    Boolean(member.orcid),
  );
  const [linkedinHasContent, setLinkedinHasContent] = useState<boolean>(
    Boolean(member.linkedin),
  );
  const formMessageContainerRef = useRef<HTMLDivElement | null>(null);
  const anchorId = getMemberAnchorId(member);
  const unselectedPublications = useMemo(
    () =>
      sortPublicationsByDate(
        publications.filter(
          (pub) => !member.publications?.some((p) => p.id === pub.id),
        ),
      ),
    [publications, member.publications],
  );
  const [randomSide] = useState<'left' | 'right'>(
    Math.random() > 0.5 ? 'left' : 'right',
  );
  const editable = useMemo(() => canEdit(user, member), [user, member]);

  const sortedUsers = useMemo(() => {
    const u = sortBy(users, 'username');
    return u;
  }, [users]);

  useEffect(() => {
    if (editable && !member.name) {
      setEditing(true);
    }
  }, [editable, member.name]);

  useEffect(() => {
    setNameHasContent(Boolean(member.name));
  }, [member.name]);

  useEffect(() => {
    setFirstnameHasContent(Boolean(member.firstname));
  }, [member.firstname]);

  useEffect(() => {
    setFunctionEnHasContent(Boolean(member.function_en));
  }, [member.function_en]);

  useEffect(() => {
    setFunctionNlHasContent(Boolean(member.function_nl));
  }, [member.function_nl]);

  useEffect(() => {
    setOrcidHasContent(Boolean(member.orcid));
  }, [member.orcid]);

  useEffect(() => {
    setLinkedinHasContent(Boolean(member.linkedin));
  }, [member.linkedin]);

  useEffect(() => {
    if (
      formMsg.message !==
      intl.formatMessage({
        id: 'GENERAL.UNSAVED_IMG',
      })
    ) {
      return;
    }
    formMessageContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [formMsg.message, intl]);

  const updateName = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            name: target.textContent ?? '',
          } as IMember,
          file: file,
          member: member,
        }),
      );
    },
    [dispatch, member, file],
  );

  const handleNameInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setNameHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const handleFirstnameInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setFirstnameHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const updateFirstname = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            firstname: target.textContent ?? '',
          } as IMember,
          file: file,
          member: member,
        }),
      );
    },
    [dispatch, member, file],
  );

  const handleFunctionEnInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setFunctionEnHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const updateFunctionEn = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            function_en: target.textContent ?? '',
          } as IMember,
          file: file,
          member: member,
        }),
      );
    },
    [dispatch, member, file],
  );

  const handleFunctionNlInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setFunctionNlHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const updateFunctionNl = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            function_nl: target.textContent ?? '',
          } as IMember,
          file: file,
          member: member,
        }),
      );
    },
    [dispatch, member, file],
  );

  const handleOrcidInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setOrcidHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const updateOrcid = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            orcid: target.textContent ?? '',
          } as IMember,
          file: file,
          member: member,
        }),
      );
    },
    [dispatch, member, file],
  );

  const handleLinkedinInput = useCallback(
    (e: React.InputEvent<HTMLHeadingElement>) => {
      setLinkedinHasContent((e.currentTarget.textContent ?? '').length > 0);
    },
    [],
  );

  const updateLinkedin = useCallback(
    (e: React.FocusEvent<HTMLHeadingElement>) => {
      const target = e.currentTarget;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            linkedin: target.textContent ?? '',
          } as IMember,
          file: file,
          member: member,
        }),
      );
    },
    [dispatch, member, file],
  );

  const handleUserChange = useCallback(
    (e: SyntheticEvent<HTMLSelectElement>) => {
      const selectedUser = users.find(
        (user) => user.id === Number(e.currentTarget.value),
      );
      if (!selectedUser) return;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            user: selectedUser,
          } as IMember,
        }),
      );
    },
    [dispatch, member, users],
  );

  const handleStatusChange = useCallback(
    (e: SyntheticEvent<HTMLSelectElement>) => {
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            status: e.currentTarget.value as IMember['status'],
          } as IMember,
        }),
      );
    },
    [dispatch, member],
  );

  const updateText_en = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      console.log('member:', member);
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            text_en: e.currentTarget.value,
          } as IMember,
        }),
      );
    },
    [dispatch, member],
  );

  const updateText_nl = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      console.log('member:', member);
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            text_nl: e.currentTarget.value,
          } as IMember,
        }),
      );
    },
    [dispatch, member],
  );

  const updateStartDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            startDate: new Date(e.currentTarget.value),
          } as IMember,
        }),
      );
    },
    [dispatch, member],
  );

  const updateEndDate = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const d = e.currentTarget.value?.length
        ? new Date(e.currentTarget.value)
        : null;
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            endDate: d,
          } as IMember,
        }),
      );
    },
    [dispatch, member],
  );

  const handleRemoveMember = useCallback(() => {
    function doDelete(): boolean {
      dispatch(
        deleteMemberAsync({
          id: member.id!,
        }),
      ).then((response: any) => {
        switch (response.payload?.response.status) {
          case 204:
            break;
          case 500:
          default:
            console.log(response);
        }
      });
      return true;
    }
    confirmDelete({
      prompts: {
        prompt1: (
          <FormattedMessage
            id="TEAM.DELETE_CONFIRM"
            values={{ member: member.name }}
          />
        ),
        prompt2: <FormattedMessage id="TEAM.DELETE_CONFIRM_2" />,
      },
      again: { serious: true },
    }).then((confirmed: boolean) => {
      if (confirmed) doDelete();
    });
  }, [dispatch, member, confirmDelete]);

  const handleImageSelect = useCallback<
    Dispatch<SetStateAction<File | undefined>>
  >((file) => {
    setFile(file);
    if (file)
      setSaved(false); // mark as not saved when a new file is selected
    else setSaved(true);
  }, []);

  const handleImageUpload = useCallback(() => {
    dispatch(
      patchMemberAsync({
        data: {
          ...member,
        } as IMember,
        file: file,
        member: member,
      }),
    ).then((response: any) => {
      switch (response.payload?.response.status) {
        case 200:
          setSaved(true);
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'GENERAL.IMAGE_UPLOAD_SUCCESS',
              }),
              success: true,
              timeout: 5000,
            },
            setFormMsg,
          );
          break;
        case 500:
        default:
          doFormMsg(
            {
              message: intl.formatMessage({
                id: 'GENERAL.IMAGE_UPLOAD_FAILED',
              }),
              success: false,
            },
            setFormMsg,
          );
          break;
      }
    });
  }, [dispatch, member, file]);

  const handleDeleteImage = useCallback(() => {
    dispatch(
      patchMemberAsync({
        data: {
          ...member,
          image: null,
        } as IMember,
        member: member,
      }),
    ).then((response: any) => {
      switch (response.payload?.response.status) {
        case 200:
          setPreviewSrc(null);
          setFile(undefined);
          setSaved(true);
          break;
        case 500:
        default:
          console.log(response);
          break;
      }
    });
  }, [dispatch, member]);

  const handleToggleEditing = useCallback(() => {
    if (editing && saved !== true && file) {
      doFormMsg(
        {
          message: intl.formatMessage({
            id: 'GENERAL.UNSAVED_IMG',
          }),
          success: false,
          timeout: 5000,
        },
        setFormMsg,
      );
      return;
    }
    setEditing((currentEditing) => !currentEditing);
  }, [editing, intl, saved, file]);

  const handleAddPublication = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = Number(e.currentTarget.value);
      const publicationToAdd = publications.find(
        (pub) => pub.id === selectedId,
      );
      if (publicationToAdd) {
        dispatch(
          patchMemberAsync({
            data: {
              ...member,
              publications: [...(member.publications || []), publicationToAdd],
            } as IMember,
          }),
        );
        e.currentTarget.value = '';
      }
    },
    [dispatch, member, publications],
  );

  const handleRemovePublication = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedId = Number(e.currentTarget.value);
      dispatch(
        patchMemberAsync({
          data: {
            ...member,
            publications: (member.publications || []).filter(
              (p) => p.id !== selectedId,
            ),
          } as IMember,
        }),
      );
      e.currentTarget.value = '';
    },
    [dispatch, member],
  );

  const isEditing = editable && editing;

  return (
    <div className="item_container">
      <div
        className={classNames(`member ${className ?? ''}`, {
          editing: isEditing,
        })}
      >
        {notfound || !member ? (
          <Error404 />
        ) : (
          <>
            {isEditing && !asPicture ? (
              <div id={anchorId}>
                <LiveContentWarning editing={editing} />
                {userIsManager ? (
                  <select
                    name="member_user"
                    className="page_author_select"
                    value={member.user?.id ?? ''}
                    // onInput={handleUserChange}
                    onChange={handleUserChange}
                  >
                    <option value="" disabled>
                      {intl.formatMessage({ id: 'GENERAL.AUTHOR' })}
                    </option>
                    {sortedUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                ) : null}
                <section className="page_header">
                  <h2
                    className={classNames('page_title', {
                      plch: !nameHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'USERS.PROFILE.NAME_PLCH',
                    })}
                    onInput={handleNameInput}
                    onBlur={updateName}
                  >
                    {member.name ?? ''}
                  </h2>
                </section>
                <section className="member_details">
                  <div
                    className={classNames('member_firstname', {
                      plch: !firstnameHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'USERS.PROFILE.FIRSTNAME_PLCH',
                    })}
                    onInput={handleFirstnameInput}
                    onBlur={updateFirstname}
                  >
                    {member.firstname ?? ''}
                  </div>
                  <div
                    className={classNames('member_orcid', {
                      plch: !orcidHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'USERS.PROFILE.ORCID_PLCH',
                    })}
                    onInput={handleOrcidInput}
                    onBlur={updateOrcid}
                  >
                    {member.orcid ?? ''}
                  </div>
                  <div
                    className={classNames('member_linkedin', {
                      plch: !linkedinHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'USERS.PROFILE.LINKEDIN_PLCH',
                    })}
                    onInput={handleLinkedinInput}
                    onBlur={updateLinkedin}
                  >
                    {member.linkedin ?? ''}
                  </div>
                </section>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.LOCALE.EN" />
                </div>
                <section className="page_body">
                  <div
                    className={classNames('member_function', {
                      plch: !functionEnHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'USERS.PROFILE.FUNCTION_PLCH',
                    })}
                    onInput={handleFunctionEnInput}
                    onBlur={updateFunctionEn}
                  >
                    {member.function_en}
                  </div>
                  <textarea
                    name="member_text_en"
                    className="page_text"
                    defaultValue={member.text_en}
                    onBlur={updateText_en}
                  />
                  <MarkdownExplainer />
                </section>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.LOCALE.NL" />
                </div>
                <section className="page_body">
                  <div
                    className={classNames('member_function', {
                      plch: !functionNlHasContent,
                    })}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={intl.formatMessage({
                      id: 'USERS.PROFILE.FUNCTION_PLCH',
                    })}
                    onInput={handleFunctionNlInput}
                    onBlur={updateFunctionNl}
                  >
                    {member.function_nl}
                  </div>
                  <textarea
                    name="member_text_nl"
                    className="page_text"
                    defaultValue={member.text_nl}
                    onBlur={updateText_nl}
                  />
                </section>
                {userIsManager ? (
                  <>
                    <div className="settings_label">
                      <FormattedMessage id="GENERAL.STATUS.STATUS" />
                    </div>
                    <div className="project_dates">
                      <div className="settings_label inline">
                        <FormattedMessage id="GENERAL.STATUS.STATUS" />
                      </div>
                      <select
                        name="member_status"
                        className="page_author_select"
                        value={member.status}
                        onChange={handleStatusChange}
                      >
                        {MEMBER_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {intl.formatMessage({
                              id: `GENERAL.STATUS.${status}`.toUpperCase(),
                            })}
                          </option>
                        ))}
                      </select>
                      <div className="settings_label inline">
                        <FormattedMessage id="GENERAL.DATES.START" />
                      </div>
                      <input
                        className="page_text"
                        type="date"
                        defaultValue={toDateInputValue(member.startDate)}
                        onBlur={updateStartDate}
                      />
                      <div className="settings_label inline">
                        <FormattedMessage id="GENERAL.DATES.END" />
                      </div>
                      <input
                        className="page_text"
                        type="date"
                        defaultValue={toDateInputValue(member.endDate)}
                        onBlur={updateEndDate}
                      />
                    </div>
                  </>
                ) : null}
                <div className="settings_label">
                  <FormattedMessage id="TEAM.PUBLICATIONS" />
                </div>
                <div className="items_dual_select">
                  <div className="items_column">
                    <div className="settings_label inline">
                      <FormattedMessage id="PUBLICATIONS.SELECT" />
                    </div>
                    <Form.Select
                      name="available_publications"
                      className="items_select"
                      value=""
                      htmlSize={5}
                      onChange={handleAddPublication}
                    >
                      <option value="">-- Select to add --</option>
                      {unselectedPublications.map((publication) => (
                        <option
                          key={publication.id}
                          value={publication.id}
                          title={`${new Date(
                            publication.publicationDate!,
                          ).getFullYear()} ${publication.title} (id: ${publication.id})`}
                        >
                          {new Date(publication.publicationDate!).getFullYear()}{' '}
                          {publication.title} (id: {publication.id})
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="items_column">
                    <div className="settings_label inline">
                      <FormattedMessage id="TEAM.PUBLICATIONS" />
                    </div>
                    <Form.Select
                      name="selected_publications"
                      className="items_select"
                      value=""
                      htmlSize={5}
                      onChange={handleRemovePublication}
                    >
                      <option value="">-- Select to remove --</option>
                      {sortPublicationsByDate(member.publications || []).map(
                        (publication) => (
                          <option
                            key={publication.id}
                            value={publication.id}
                            title={`${new Date(
                              publication.publicationDate!,
                            ).getFullYear()} ${publication.title} (id: ${publication.id})`}
                          >
                            {new Date(
                              publication.publicationDate!,
                            ).getFullYear()}{' '}
                            {publication.title} (id: {publication.id})
                          </option>
                        ),
                      )}
                    </Form.Select>
                  </div>
                </div>
                <div className="settings_label">
                  <FormattedMessage id="GENERAL.IMAGE" />
                </div>
                <div
                  ref={formMessageContainerRef}
                  className="form_message_container"
                >
                  <FormMessage {...formMsg} />
                </div>
                <div className="image_settings">
                  <div className="page_image editing">
                    <FileUpload
                      types={imageFileTypes}
                      currentSrc={member.image?.uri || null}
                      previewSrc={previewSrc}
                      size={200}
                      defaultImg={null}
                      setPreviewSrc={setPreviewSrc}
                      showTrigger={true}
                      showMsg={(m) => doFormMsg(m, setFormMsg)}
                      setFile={handleImageSelect}
                      onDelete={handleDeleteImage}
                      onSave={handleImageUpload}
                      saved={saved}
                      confirmationText={
                        <FormattedMessage id="GENERAL.DELETE_IMAGE_CNF" />
                      }
                      disabled={!editing}
                    />
                  </div>
                </div>
              </div>
            ) : asPicture ? (
              <a href={`#${anchorId}`}>
                {member.image ? (
                  <div className="page_image">
                    <img src={(member.image as IFile).uri} alt={member.name} />
                  </div>
                ) : null}
                <h4 className="member_name">{member.name}</h4>
                <div className="member_function">
                  {locale === 'en' ? member.function_en : member.function_nl}
                </div>
              </a>
            ) : (
              <>
                <section className="page_header" id={anchorId}>
                  <h2 className="member_name">{member.name}</h2>
                  <div className="member_function">
                    {locale === 'en' ? member.function_en : member.function_nl}
                    {userIsManager && member.startDate ? (
                      <>
                        {', '}
                        <FormattedMessage
                          id="GENERAL.SINCE"
                          values={{
                            date: new Date(
                              member.startDate,
                            ).toLocaleDateString(),
                          }}
                        />
                      </>
                    ) : null}
                  </div>
                </section>
                <section className="page_body">
                  {member.image ? (
                    <div className={`page_image ${randomSide}`}>
                      <img
                        src={(member.image as IFile).uri}
                        alt={member.name}
                      />
                    </div>
                  ) : null}
                  {locale === 'en' ? (
                    <MarkdownContent className="page_text">
                      {member.text_en}
                    </MarkdownContent>
                  ) : (
                    <MarkdownContent className="page_text">
                      {member.text_nl}
                    </MarkdownContent>
                  )}
                </section>
                {member.orcid || member.linkedin ? (
                  <section className="member_links">
                    {member.orcid ? (
                      <div className="member_orcid">
                        <a
                          className="cta"
                          href={member.orcid}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FormattedMessage id="USERS.PROFILE.ORCID" />
                        </a>
                      </div>
                    ) : null}
                    {member.linkedin ? (
                      <div className="member_linkedin">
                        <a
                          className="cta"
                          href={member.linkedin}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <FormattedMessage id="USERS.PROFILE.LINKEDIN" />
                        </a>
                      </div>
                    ) : null}
                  </section>
                ) : null}
                <section className="page_body">
                  {member.publications && member.publications.length > 0 ? (
                    <div className="items_links">
                      <span className="label">
                        <FormattedMessage id="TEAM.PUBLICATIONS" />
                      </span>
                      {sortPublicationsByDate(member.publications).map(
                        (publication) => (
                          <a
                            href={
                              '/publications#' +
                              getPublicationAnchorId(publication)
                            }
                            key={publication.id}
                          >
                            <MarkdownContent key={publication.id}>
                              {getPublicationCitation(publication) ?? ''}
                            </MarkdownContent>
                          </a>
                        ),
                      )}
                    </div>
                  ) : null}
                </section>
              </>
            )}
          </>
        )}
      </div>
      {user && !asPicture ? (
        <div className="section_menu">
          {editable ? (
            <Icon
              symbol={editing ? IconSymbol.visible : IconSymbol.pencil}
              onClick={handleToggleEditing}
              size={20}
              hintProps={{
                hint: editing
                  ? { id: 'PAGES.PREVIEW_MODE' }
                  : { id: 'PAGES.EDIT_MODE' },
              }}
            />
          ) : null}
          {userIsManager ? (
            <>
              <Icon
                inline
                className="icon_button"
                symbol={IconSymbol.bin}
                size={18}
                onClick={() => handleRemoveMember()}
                hintProps={{ hint: { id: 'TEAM.REMOVE_MEMBER' } }}
              />
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
